import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  getCookieConsent, 
  saveCookieConsent, 
  hasConsent, 
  getDefaultSettings,
  initializeAnalytics,
  clearNonNecessaryCookies,
  COOKIE_CATEGORIES 
} from '../utils/cookieUtils';
import { initGA, disableGA } from '../utils/analytics';

const CookieContext = createContext();

export const useCookie = () => {
  const context = useContext(CookieContext);
  if (!context) {
    throw new Error('useCookie must be used within a CookieProvider');
  }
  return context;
};

export const CookieProvider = ({ children }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState(getDefaultSettings());
  const [isLoading, setIsLoading] = useState(true);

  // Initialize cookie consent on mount
  useEffect(() => {
    const initializeConsent = () => {
      const existingConsent = getCookieConsent();
      
      if (existingConsent) {
        setSettings(existingConsent);
        setShowBanner(false);
        initializeAnalytics();
      } else {
        setShowBanner(true);
      }
      
      setIsLoading(false);
    };

    // Listen for custom event to open cookie settings
    const handleOpenCookieSettings = () => {
      setShowSettings(true);
      setShowBanner(false);
    };

    window.addEventListener('openCookieSettings', handleOpenCookieSettings);

    initializeConsent();

    return () => {
      window.removeEventListener('openCookieSettings', handleOpenCookieSettings);
    };
  }, []);

  // Update analytics when settings change
  useEffect(() => {
    if (!isLoading) {
      initializeAnalytics();
      // Initialize or disable GA based on consent
      if (settings[COOKIE_CATEGORIES.ANALYTICS]) {
        initGA();
      } else {
        disableGA();
      }
    }
  }, [settings, isLoading]);

  const acceptAll = () => {
    const allAccepted = {
      [COOKIE_CATEGORIES.NECESSARY]: true,
      [COOKIE_CATEGORIES.ANALYTICS]: true,
      [COOKIE_CATEGORIES.FUNCTIONAL]: true,
      [COOKIE_CATEGORIES.MARKETING]: true
    };
    
    setSettings(allAccepted);
    saveCookieConsent(allAccepted);
    setShowBanner(false);
    setShowSettings(false);
  };

  const declineAll = () => {
    const necessaryOnly = {
      [COOKIE_CATEGORIES.NECESSARY]: true,
      [COOKIE_CATEGORIES.ANALYTICS]: false,
      [COOKIE_CATEGORIES.FUNCTIONAL]: false,
      [COOKIE_CATEGORIES.MARKETING]: false
    };
    
    setSettings(necessaryOnly);
    saveCookieConsent(necessaryOnly);
    setShowBanner(false);
    setShowSettings(false);
    clearNonNecessaryCookies();
    // Disable GA when declining all
    disableGA();
  };

  const saveSettings = (newSettings) => {
    setSettings(newSettings);
    saveCookieConsent(newSettings);
    setShowSettings(false);
    clearNonNecessaryCookies();
    // Handle GA based on analytics consent
    if (newSettings[COOKIE_CATEGORIES.ANALYTICS]) {
      initGA();
    } else {
      disableGA();
    }
  };

  const updateSetting = (category, value) => {
    const newSettings = { ...settings, [category]: value };
    setSettings(newSettings);
  };

  const openSettings = () => {
    setShowSettings(true);
    setShowBanner(false);
  };

  const closeSettings = () => {
    setShowSettings(false);
    // If no consent was given before, show banner again
    if (!hasConsent()) {
      setShowBanner(true);
    }
  };

  const resetConsent = () => {
    localStorage.removeItem('cookie_consent');
    setSettings(getDefaultSettings());
    setShowBanner(true);
    setShowSettings(false);
    clearNonNecessaryCookies();
    // Disable GA when resetting consent
    disableGA();
  };

  const value = {
    showBanner,
    showSettings,
    settings,
    isLoading,
    acceptAll,
    declineAll,
    saveSettings,
    updateSetting,
    openSettings,
    closeSettings,
    resetConsent,
    hasConsent: hasConsent()
  };

  return (
    <CookieContext.Provider value={value}>
      {children}
    </CookieContext.Provider>
  );
};
